<template>
    <div class="s-purchasing-period">
        <p class="title" v-html="title" />
        <s-selector
            :list="options"
            :selected="period"
            @change="$emit('change', Number($event.target.value))"
        />
        <div v-if="hasNotification" class="notification">
            <p
                v-if="notificationItem.title"
                class="notification-title"
                v-html="notificationItem.title"
            />
            <ul v-if="list.length">
                <li
                    v-for="(item, itemIndex) in list"
                    :key="`item-${itemIndex}`"
                    class="notification-item"
                >
                    <a-glyph name="check-square" />
                    <span v-html="item" />
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import AGlyph from '@core/components/glyph/glyph.vue';
import analytics from '@core/mixins/analytics.js';
import currency from '@core/mixins/currency.js';
import SSelector from '../selector.vue';

export default {
    name: 'SPurchasingPeriod',

    components: {
        AGlyph,
        SSelector,
    },

    mixins: [analytics, currency],

    props: {
        dictionary: {
            type: Object,
            default: null,
        },
        licenses: {
            type: Array,
            default: null,
        },
        selector: {
            type: Object,
            default: null,
        },
        period: {
            type: Number,
            default: 1,
        },
        quantity: {
            type: Object,
            default: null,
        },
        promo: {
            type: Object,
            default: null,
        },
    },

    emits: ['change'],

    computed: {
        title() {
            const text = 'Subscription period';
            return this.dictionary['Subscription period'] || text;
        },
        hasNotification() {
            return this.period && this.list.length;
        },
        notificationItem() {
            const period = this.period;
            const list = this.licenses.find((item) => item.period === period) || {};
            return list.notification || {};
        },
        list() {
            return this.notificationItem?.list || [];
        },
        options() {
            const isPromoActive = this.promo?.isPromoActive;
            const license = this.licenses.find((item) => item.period === 1);
            const annual = this.findLicense(license);
            const quantityMin = Math.min(...this.quantity?.list || [0]);
            const licenses = quantityMin ? this.licenses.filter((item) => item.quantity === quantityMin) : this.licenses;

            const options = licenses.map((item, i) => {
                const period = `${item.period}Y-period`;
                const placeholder = this.dictionary[period] || item.placeholder || '';
                const textOption = `<span class="option">${placeholder}</span>`;
                const result = {
                    id: item.period,
                    info: item.info || [],
                    option: textOption,
                };

                if (!i && !isPromoActive && !item.showPrice) return result;

                const current = this.findLicense(item);
                const priceFull = current.price + current.discount;
                const price = isPromoActive ? priceFull : annual.price * item.period;
                const discount = price ? Math.round((100 * (price - current.price)) / price) : 0;

                let textPromo = '';
                if (discount) {
                    const label = this.dictionary['Save XX%'];
                    textPromo = `<span class="promo">${label.replace('XX', discount)}</span>`;
                }

                let textPrice = '';
                if (item.showPrice) {
                    textPrice = `<span class="price">${this.setCurrency(priceFull)}</span>`;
                }

                return {
                    ...result,
                    option: textOption + textPromo + textPrice,
                };
            });

            return [...new Map(options.map((item) => [item.id, item])).values()];
        },
    },

    methods: {
        findLicense(product) {
            const cleverBridgeCartID = product?.cleverBridgeCartID || null;
            const license = this.licenseData({ cleverBridgeCartID, promo: this.promo });
            return { price: license.price || 0, discount: license.discount_absolute || 0 };
        },
    },
};
</script>

<style lang="postcss" scoped>
.s-purchasing-period {
    .title {
        @mixin paragraph-accent;
        margin-bottom: 16px;
        color: var(--av-nav-primary);
    }
    .notification {
        margin-top: 16px;
    }
    .notification-title {
        @mixin body-accent;
        color: var(--av-fixed-secondary);
    }
    .notification-item {
        @mixin body;
        display: flex;
        color: var(--av-fixed-secondary);
        &:deep(.a-glyph) {
            min-width: 16px;
            margin: 4px 0 0;
            vertical-align: middle;
        }
    }
}
</style>
